import { FC, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';

/**
 * Typographic building blocks for rich static content (FAQ answers, legal
 * copy). Font size / line height inherit from the surrounding container so
 * each page controls its own scale. Style-only primitives are `styled()`
 * components; callers can still override anything via `sx`.
 */

export const P = styled('p')(({ theme }) => ({
  margin: theme.spacing(0, 0, 1.5),
  '&:last-child': { marginBottom: 0 },
}));

/** Inline bold text; defaults to the body ink, pass navy for headings-like emphasis. */
export const B: FC<{ navy?: boolean; children: ReactNode }> = ({
  navy,
  children,
}) => (
  <Box
    component="strong"
    sx={{ color: navy ? 'staticPages.navy' : 'staticPages.text.body' }}
  >
    {children}
  </Box>
);

/** Small bold lead-in heading inside a content block. */
export const SubHead = styled('div')(({ theme }) => ({
  color: theme.palette.staticPages.navy,
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
}));

/** Compact light note panel used inside FAQ answers. */
export const NoteBox = styled('p')(({ theme }) => ({
  margin: theme.spacing(0, 0, 2),
  padding: theme.spacing(1.5, 2),
  background: theme.palette.staticPages.bg.callout,
  borderRadius: theme.shape.borderRadius * 2,
  fontSize: theme.typography.pxToRem(14),
}));

export const UList = styled('ul')(({ theme }) => ({
  margin: theme.spacing(0, 0, 2),
  paddingLeft: theme.spacing(3),
  '& li': { marginBottom: theme.spacing(1) },
  '& li:last-child': { marginBottom: 0 },
}));

export const OList = styled('ol')(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.spacing(3),
  '& li': { marginBottom: theme.spacing(1) },
  '& li:last-child': { marginBottom: 0 },
}));

/** Inline text link used inside static page content. */
export const TextLink = styled(Link)(({ theme }) => ({
  color: theme.palette.staticPages.blue,
  '&:hover': { color: theme.palette.staticPages.navy },
}));

TextLink.defaultProps = { underline: 'none' };
